import React, { createContext, useContext, useMemo, useState } from 'react';
import { useTracking, createFeature } from '@edreamsodigeo/frontend-core';
import {
  useFilteredItinerariesQuery,
  useSemFilteredItinerariesQuery,
} from './FilteredItinerariesProvider';
import { useSorting, useSortingTypeLabel } from '@components/Sorting/Provider';
import { isItineraryVisiblePrimeDayTab } from 'clientState/utilsHelper';
import { COGNITIVE_OVERLOAD } from 'clientState/ab/aliases';

// TODO: implement better a pagination Provider
const nextTick = () => new Promise((resolve) => setTimeout(resolve, 0));
const noop = Function.prototype;

const DisplayedItinerariesContext = createContext();
const ActionsContext = createContext({
  loadNextPage: noop,
});

export const useDisplayedItinerariesActions = () => {
  return useContext(ActionsContext);
};

export const useDisplayedItinerariesQuery = () => {
  const queryState = useContext(DisplayedItinerariesContext);
  const { loading, error, data } = queryState || {};
  return {
    ...queryState,
    itineraries: loading || error ? [] : data?.itineraries,
  };
};

const getTotalTrainResultsInFirstPage = (itineraries) => {
  return itineraries.filter((itinerary) =>
    itinerary.transportTypes.includes('TRAIN')
  ).length;
};

const getFirstTrainResultPosition = (itineraries) => {
  return (
    itineraries.findIndex((itinerary) =>
      itinerary.transportTypes.includes('TRAIN')
    ) + 1
  );
};

export const useTrainResultsOnLoadLabel = () => {
  const { itineraries } = useDisplayedItinerariesQuery();
  const sortingTypeLabel = useSortingTypeLabel();
  return [
    'onload_trains',
    getTotalTrainResultsInFirstPage(itineraries),
    itineraries.length,
    getFirstTrainResultPosition(itineraries),
    sortingTypeLabel,
  ].join('_');
};

export const useSemTrainResultsOnLoadLabel = () => {
  const { itineraries } = useDisplayedItinerariesQuery();
  return [
    'onload_trains',
    getTotalTrainResultsInFirstPage(itineraries),
    itineraries.length,
    getFirstTrainResultPosition(itineraries),
  ].join('_');
};

export const MORE_RESULTS_BUNCH = 30;
export const META_MORE_RESULTS_BUNCH = 2;

const createDisplayedItinerariesQueryState = (
  data,
  sortBy,
  { pageNumber, onDisplayedItinerariesChange },
  limitedList
) => {
  const { itineraries = [] } = data || {};
  const renderedItineraries = itineraries.filter((itinerary) =>
    isItineraryVisiblePrimeDayTab(itinerary, sortBy)
  );
  const isMeta = !!data?.externalSelection;
  const limit =
    isMeta && limitedList ? META_MORE_RESULTS_BUNCH : MORE_RESULTS_BUNCH;
  const numberOfDisplayedItineraries = pageNumber * limit;

  return {
    error: null,
    loading: false,
    data: {
      ...data,
      itineraries: itineraries.slice(0, numberOfDisplayedItineraries),
      hasNextPage: numberOfDisplayedItineraries < renderedItineraries.length,
      pageNumber: pageNumber,
      onDisplayedItinerariesChange: onDisplayedItinerariesChange,
    },
  };
};

const DisplayedItinerariesProvider = ({
  children,
  onDisplayedItinerariesChange,
  limitedList = false,
}) => {
  const { trackEvent } = useTracking();
  const queryState = useFilteredItinerariesQuery();
  const [showMoreLoading, setShowMoreLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const { sorting: sortBy } = useSorting();

  const { loading, error, data } = queryState;
  const state = createDisplayedItinerariesQueryState(
    data,
    sortBy,
    {
      pageNumber,
      onDisplayedItinerariesChange,
    },
    limitedList
  );
  const nextQueryState =
    loading || error
      ? queryState
      : { ...state, hasNextPage: state.data.hasNextPage, showMoreLoading };

  const actions = useMemo(() => {
    return {
      loadNextPage: async () => {
        setShowMoreLoading(true);
        // Simulate loading putting the next state on next tick
        await nextTick();
        setShowMoreLoading(false);
        setPageNumber((current) => {
          trackEvent({
            action: 'results_list',
            label: `page_selected_next_${current + 1}`,
          });
          return current + 1;
        });
      },
    };
  }, [setShowMoreLoading, setPageNumber, trackEvent]);

  return (
    <ActionsContext.Provider value={actions}>
      <DisplayedItinerariesContext.Provider value={nextQueryState}>
        {children}
      </DisplayedItinerariesContext.Provider>
    </ActionsContext.Provider>
  );
};

const DisplayedItinerariesProviderMeta = ({
  children,
  onDisplayedItinerariesChange,
}) => {
  return (
    <DisplayedItinerariesProvider
      onDisplayedItinerariesChange={onDisplayedItinerariesChange}
      limitedList={true}
    >
      {children}
    </DisplayedItinerariesProvider>
  );
};

/* 
  PRIME_TITANS527:
  B we want to limit the results
  D we want to hidde the sidebar filter and limit the results
*/
const DisplayedItinerariesProviderWrapper = createFeature({
  name: COGNITIVE_OVERLOAD,
  variations: [
    DisplayedItinerariesProvider,
    DisplayedItinerariesProviderMeta,
    DisplayedItinerariesProvider,
    DisplayedItinerariesProviderMeta,
  ],
  version: 1,
});

export const SEM_MORE_RESULTS_BUNCH = 10;

const createSemDisplayedItinerariesQueryState = (
  data,
  { pageNumber, onDisplayedItinerariesChange }
) => {
  const { itineraries = [] } = data || {};
  const numberOfDisplayedItineraries = pageNumber * SEM_MORE_RESULTS_BUNCH;

  return {
    error: null,
    loading: false,
    data: {
      ...data,
      itineraries: itineraries.slice(0, numberOfDisplayedItineraries),
      hasNextPage: numberOfDisplayedItineraries < itineraries.length,
      pageNumber: pageNumber,
      onDisplayedItinerariesChange: onDisplayedItinerariesChange,
    },
  };
};

const SemDisplayedItinerariesProvider = ({
  children,
  onDisplayedItinerariesChange,
}) => {
  const { trackEvent } = useTracking();
  const queryState = useSemFilteredItinerariesQuery();
  const [showMoreLoading, setShowMoreLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);

  const { loading, error, data } = queryState;
  const state = createSemDisplayedItinerariesQueryState(data, {
    pageNumber,
    onDisplayedItinerariesChange,
  });
  const nextQueryState =
    loading || error
      ? queryState
      : { ...state, hasNextPage: state.data.hasNextPage, showMoreLoading };

  const actions = useMemo(() => {
    return {
      loadNextPage: async () => {
        setShowMoreLoading(true);
        // Simulate loading putting the next state on next tick
        await nextTick();
        setShowMoreLoading(false);
        setPageNumber((current) => {
          trackEvent({
            action: 'results_list',
            label: `page_selected_next_${current + 1}`,
          });
          return current + 1;
        });
      },
    };
  }, [setShowMoreLoading, setPageNumber, trackEvent]);

  return (
    <ActionsContext.Provider value={actions}>
      <DisplayedItinerariesContext.Provider value={nextQueryState}>
        {children}
      </DisplayedItinerariesContext.Provider>
    </ActionsContext.Provider>
  );
};

export {
  DisplayedItinerariesProviderWrapper as default,
  SemDisplayedItinerariesProvider,
};
